import React, { useContext, useEffect } from 'react';
import { ChangesContext, SiteContext, UserContext } from '../../Contexts';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faImages,
    faPhotoFilm,
    faListUl,
    faFaceSmile,
    faFileLines,
    faNewspaper,
    faFile,
    faPlus,
    faGlobe,
    faShieldHalved,
    faLanguage,
} from '@fortawesome/free-solid-svg-icons';
import { Utils } from '../../Services/Utils';
import { Zigzag, ZigzagStrip } from '../../Themes/Brand';
import { EntityColors } from '../../Themes/EntityColors';
import CustomTooltip from '../Tooltips/CustomTooltip';

/* ── Data ─────────────────────────────────────────────────────────────── */

interface EntityDef {
    title: string;
    description: string;
    entity: string;
    accentColor: string;
    createLink: boolean;
    createLabel?: string;
    createHref?: string;
    icon: any;
    group: 'content' | 'media' | 'admin';
}

const entityDefs: EntityDef[] = [
    {
        title: 'Príspevky',
        description: 'Tvorba a správa blogových príspevkov',
        entity: 'post',
        accentColor: EntityColors.blog.action,
        createLink: true,
        icon: faNewspaper,
        group: 'content',
    },
    {
        title: 'Galérie',
        description: 'Správa fotogalérií a albumov',
        entity: 'gallery',
        accentColor: EntityColors.gallery.action,
        createLink: true,
        icon: faPhotoFilm,
        group: 'content',
    },
    {
        title: 'Stránky',
        description: 'Štruktúra a textový obsah stránok',
        entity: 'page',
        accentColor: EntityColors.page.action,
        createLink: false,
        icon: faFileLines,
        group: 'content',
    },
    {
        title: 'Zoznamy',
        description: 'Navigačné a obsahové zoznamy',
        entity: 'list',
        accentColor: EntityColors.list.action,
        createLink: false,
        icon: faListUl,
        group: 'content',
    },
    {
        title: 'Jazyky',
        description: 'Jazyky stránky',
        entity: 'language',
        accentColor: EntityColors.language.action,
        createLink: false,
        icon: faLanguage,
        group: 'content',
    },
    {
        title: 'Obrázky',
        description: 'Nahrávanie a správa mediálnych súborov',
        entity: 'image',
        accentColor: EntityColors.image.action,
        createLink: true,
        createLabel: 'Nahrať',
        createHref: '/image/add',
        icon: faImages,
        group: 'media',
    },
    {
        title: 'Súbory',
        description: 'Nahrávanie a správa dokumentov (PDF, Word, Excel...)',
        entity: 'file',
        accentColor: EntityColors.file.action,
        createLink: true,
        createLabel: 'Nahrať',
        createHref: '/file/add',
        icon: faFile,
        group: 'media',
    },
    {
        title: 'Používatelia',
        description: 'Správa prístupu a používateľských účtov',
        entity: 'user',
        accentColor: EntityColors.user.action,
        createLink: true,
        icon: faFaceSmile,
        group: 'admin',
    },
    {
        title: 'Weby',
        description: 'Prehľad webov a rebuild frontend databázy',
        entity: 'website',
        accentColor: EntityColors.website.action,
        createLink: false,
        icon: faGlobe,
        group: 'admin',
    },
];

const groups = [
    { key: 'content', label: 'Obsah' },
    { key: 'media',   label: 'Médiá' },
    { key: 'admin',   label: 'Správa' },
] as const;

const CONTENT_WIDTH = '78rem';

function greetingForHour(hour: number): string {
    if (hour < 5) return 'Dobrú noc';
    if (hour < 12) return 'Dobré ráno';
    if (hour < 18) return 'Dobré popoludnie';
    return 'Dobrý večer';
}

/* ── Component ────────────────────────────────────────────────────────── */

const HomeComponent = () => {
    useEffect(() => {
        Utils.setDocumentTitle('Domov');
    }, []);

    const { currentSite }: any = useContext(SiteContext);
    const { currentUser }: any = useContext(UserContext);
    const { hasChanges } = useContext(ChangesContext);

    const { usesPost = false, usesGallery = false } = currentSite.settings || {};
    const sitePicked = Utils.hasUserPickedWebsite(currentSite.id);

    const visible = entityDefs.filter(e => {
        if (['image', 'file', 'text', 'list', 'page', 'language'].includes(e.entity) && (currentUser?.websites?.length ?? 0) === 0) return false;
        if (e.entity === 'post' && !usesPost) return false;
        if (e.entity === 'gallery' && !usesGallery) return false;
        if (e.entity === 'user' && !currentUser?.isSuperAdmin) return false;
        if (e.entity === 'website' && !currentUser?.isSuperAdmin) return false;
        return true;
    });

    const today = new Date().toLocaleDateString('sk-SK', {
        weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    });
    const greeting = currentUser?.displayName
        ? `${greetingForHour(new Date().getHours())}, ${currentUser.displayName}`
        : greetingForHour(new Date().getHours());

    return (
        <div style={{ minHeight: '100%', backgroundColor: 'var(--st-canvas)' }}>

            {/* ── Hero: the lobby between the ink chrome and the paper workspace ── */}
            <div style={{ position: 'relative', backgroundColor: 'var(--st-ink)', overflow: 'hidden' }}>
                <HeroWebMotif />
                <div style={{
                    position: 'relative',
                    maxWidth: CONTENT_WIDTH,
                    margin: '0 auto',
                    padding: '2.2rem 2rem 2.4rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: '1.25rem',
                }}>
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.55rem' }}>
                            <FontAwesomeIcon
                                icon={faGlobe}
                                style={{ color: sitePicked ? 'rgba(207,199,228,0.5)' : '#8B77E8', fontSize: '0.72rem' }}
                            />
                            <span className="st-mono" style={{
                                color: sitePicked ? 'rgba(207,199,228,0.5)' : '#8B77E8',
                                letterSpacing: '0.06em',
                                fontWeight: sitePicked ? 400 : 600,
                            }}>
                                {sitePicked ? currentSite.domainName : 'Vyberte stránku v paneli vľavo'}
                            </span>
                        </div>
                        <h1 style={{
                            fontFamily: 'var(--st-font-display)',
                            fontSize: 'clamp(1.55rem, 1.1rem + 1.8vw, 2.1rem)',
                            fontWeight: 700,
                            color: '#F4F4F5',
                            margin: 0,
                            letterSpacing: '-0.01em',
                        }}>
                            {greeting}
                        </h1>
                    </div>
                </div>

                <ZigzagStrip color="#6D57D4" />
            </div>

            {/* ── Launcher grid ─────────────────────────────────────────────── */}
            <div style={{ maxWidth: CONTENT_WIDTH, margin: '0 auto', padding: '1.9rem 2rem 3.5rem' }}>
                {visible.length === 0 ? (
                    <NoAccessState />
                ) : (
                    groups.map(group => {
                        const items = visible.filter(e => e.group === group.key);
                        if (items.length === 0) return null;

                        return (
                            <div key={group.key} style={{ marginBottom: '2rem' }}>
                                <p className="st-eyebrow" style={{
                                    color: 'var(--st-text-faint)',
                                    margin: '0 0 0.75rem 0.1rem',
                                }}>
                                    {group.label}
                                </p>

                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(15rem, 1fr))',
                                    gap: '0.9rem',
                                }}>
                                    {items.map(entity => (
                                        <EntityTile key={entity.entity} entity={entity} />
                                    ))}
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
};

/* ── Hero sub-pieces ──────────────────────────────────────────────────── */

/**
 * A quiet, static echo of the login screen's orb web — a corner of silk
 * spokes and thread-arcs at rest, now that the visitor is already inside.
 */
const HeroWebMotif: React.FC = () => {
    const hub = { x: 380, y: 20 };
    const spokeAnglesDeg = [95, 118, 141, 164, 187, 210, 233, 256];
    const spokeLength = 340;
    const arcRadii = [55, 120, 195, 280, 370];

    return (
        <svg
            aria-hidden="true"
            viewBox="0 0 400 400"
            preserveAspectRatio="xMaxYMin slice"
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
        >
            {arcRadii.map(r => (
                <circle key={r} cx={hub.x} cy={hub.y} r={r} fill="none" stroke="#CFC7E4" strokeOpacity={0.09} strokeWidth={1} />
            ))}
            {spokeAnglesDeg.map(deg => {
                const rad = (deg * Math.PI) / 180;
                return (
                    <line
                        key={deg}
                        x1={hub.x}
                        y1={hub.y}
                        x2={hub.x + Math.cos(rad) * spokeLength}
                        y2={hub.y + Math.sin(rad) * spokeLength}
                        stroke="#CFC7E4"
                        strokeOpacity={0.09}
                        strokeWidth={1}
                    />
                );
            })}
            <circle cx={hub.x} cy={hub.y} r={3} fill="#8B77E8" fillOpacity={0.4} />
        </svg>
    );
};

/* ── Launcher sub-pieces ──────────────────────────────────────────────── */

const EntityTile: React.FC<{ entity: EntityDef }> = ({ entity }) => {
    const [hovered, setHovered] = React.useState(false);

    return (
        <div
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
                position: 'relative',
                borderRadius: 'var(--st-radius-card)',
                border: `1px solid ${hovered ? entity.accentColor + '55' : entity.accentColor + '22'}`,
                backgroundColor: hovered ? entity.accentColor + '14' : entity.accentColor + '0C',
                boxShadow: hovered ? 'var(--st-shadow-card)' : 'none',
                transform: hovered ? 'translateY(-2px)' : 'none',
                transition: 'background-color 0.15s, border-color 0.15s, transform 0.15s, box-shadow 0.15s',
            }}
        >
            <Link
                to={`/${entity.entity}/table`}
                style={{ display: 'block', padding: '1.1rem 1.15rem', textDecoration: 'none' }}
            >
                <div style={{
                    width: '38px',
                    height: '38px',
                    borderRadius: '10px',
                    backgroundColor: entity.accentColor + '1E',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '0.75rem',
                }}>
                    <FontAwesomeIcon icon={entity.icon} style={{ color: entity.accentColor, fontSize: '0.95rem' }} />
                </div>
                <p style={{
                    fontFamily: 'var(--st-font-display)',
                    fontWeight: 700,
                    color: 'var(--st-text)',
                    margin: 0,
                    fontSize: '0.94rem',
                    paddingRight: entity.createLink ? '2rem' : 0,
                }}>
                    {entity.title}
                </p>
                <p style={{ color: 'var(--st-text-soft)', margin: '0.25rem 0 0', fontSize: '0.78rem', lineHeight: 1.4 }}>
                    {entity.description}
                </p>
            </Link>

            {entity.createLink && (
                <CustomTooltip placement="top" message={entity.createLabel || 'Vytvoriť'} tooltipId={`home-create-${entity.entity}`}>
                    <Link
                        to={entity.createHref || `/${entity.entity}/create`}
                        className="st-btn st-btn--fill st-btn--icon st-btn--sm"
                        style={{
                            '--btn-accent': entity.accentColor,
                            position: 'absolute',
                            top: '0.85rem',
                            right: '0.85rem',
                        } as React.CSSProperties}
                    >
                        <FontAwesomeIcon icon={faPlus} style={{ fontSize: '0.65rem' }} />
                    </Link>
                </CustomTooltip>
            )}
        </div>
    );
};

const NoAccessState: React.FC = () => (
    <div style={{
        textAlign: 'center',
        padding: '3.5rem 1.5rem',
        backgroundColor: 'var(--st-paper)',
        borderRadius: 'var(--st-radius-card)',
        boxShadow: 'var(--st-shadow-card)',
    }}>
        <Zigzag color="#6D57D466" width={44} height={9} style={{ marginBottom: '0.9rem' }} />
        <p style={{
            fontFamily: 'var(--st-font-display)',
            fontWeight: 700,
            color: 'var(--st-text)',
            margin: 0,
            fontSize: '1rem',
        }}>
            Zatiaľ nemáte priradenú žiadnu stránku
        </p>
        <p style={{ color: 'var(--st-text-faint)', margin: '0.4rem 0 0', fontSize: '0.85rem' }}>
            Obráťte sa na správcu, aby vám pridelil prístup k stránke.
        </p>
    </div>
);

export default HomeComponent;
